import { service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotel, Ota } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type AjaxService from 'lumen/services/ajax';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import type {
  MembershipType,
  RateType,
} from 'lumen/utils/constants/general-constants';

export interface LiveupdateCompareParams {
  los: number;
  membershiptype: MembershipType;
  persons: number;
  platform: number;
  pos: string;

  rate_type: RateType;
}

export type LiveupdateParams = {
  compare_params?: LiveupdateCompareParams;
} & LiveupdateCompareParams;

export default class Liveupdate extends Model {
  [Type] = 'liveupdate' as const;

  @belongsTo('hotel', { async: false, inverse: null })
  declare hotel: Hotel;

  @belongsTo('ota', { async: false, inverse: null })
  declare ota: Ota;

  @attr('number') declare bulkLiveupdateId: number;
  @attr('dayjs') declare completionTimestamp: DayjsTransformReturn;
  @attr('boolean') declare customRange: boolean;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare labels: string;
  @attr() declare params: LiveupdateParams;
  @attr('number') declare priority: number;
  @attr('boolean') declare sendEmail: boolean;
  @attr('dayjs') declare startTimestamp: NonNullable<DayjsTransformReturn>;
  @attr('number') declare status: number;
  @attr('hoteldate') declare toDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare type: string;

  @service declare ajax: AjaxService;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCustom() {
    return this.sendEmail;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDayLiveupdate() {
    return this.fromDate.isSame(this.toDate, 'day');
  }

  // @ts-expect-error: Property 'save' in type 'Liveupdate' is not assignable to the same property in base type 'Model'.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  save() {
    // Serialize this model's data (snake-casing etc..)
    const data = this.serialize();

    // We override the save method to be able to post to apigateway instead of api/v3
    return this.ajax.request(
      `${this.ajax?.host}/apigateway/v1/app/liveshop?subscription_id=${this.hotel.id}`,
      {
        data: JSON.stringify({
          liveupdate: data,
        }),
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
      },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateStatus(code: number) {
    this.status = code;

    // We update the status via the old API manually because ember-data POSTS instead of PUT
    return this.ajax.request(
      `${this.ajax?.host}/${this.ajax?.namespace}/liveupdates/${this.id}`,
      {
        data: JSON.stringify({
          liveupdate: {
            id: this.id,
            status: code,
            hotel: this.hotel?.id,
          },
        }),
        method: 'PUT',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
      },
    );
  }
}
